import Axios from 'axios';

import { getBaseUrl } from '@libs/NwUtils';

import { getArtMediaDocumentId } from '@containers/Art/ArtDocuments';
import {
    NEWS_LINK_TARGET,
    buildNewsLinkTargetFromApi,
    normalizeNewsLinkTarget,
    normalizeNewsStatus,
    toApiNewsLinkTargetType,
    toApiNewsStatus,
    toApiNewsStatusFilter,
} from '@containers/Art/News/News.constants';
import { normalizeNewsFormats } from '@containers/Art/News/NewsFormats.config';

export const NEWS_SAVE_MODE = {
    API: 'api',
};

const NEWS_API_BASE = '/cms/newsitems';
const NEWS_SEARCH_API = '/cms/newsitems/search';
const NEWS_SORT_API = '/cms/newsitems/sort';
const NEWS_SECTIONS_API_BASE = '/cms/newssections';
const NEWS_TAGS_API_URL = '/art/news/tags';

/** Thrown when a section still contains news items. */
export const NEWS_SECTION_NOT_EMPTY = 'NEWS_SECTION_NOT_EMPTY';

const normalizeName = (value) => {
    return String(value || '')
        .trim()
        .replace(/\s+/g, ' ');
};

const getSafeNewsSectionOrder = (sectionOrder) => {
    const numericSectionOrder = Number(sectionOrder);
    return Number.isFinite(numericSectionOrder) ? numericSectionOrder : undefined;
};

const normalizeApiResponse = (response) => {
    return response && response.data !== undefined ? response.data : response;
};

const getJsonRequestHeaders = () => {
    const headers = {
        'Content-Type': 'application/json; charset=utf-8',
    };

    try {
        const currentUser = JSON.parse(sessionStorage.getItem('currentUser'));
        if (currentUser?.Token) {
            headers.Authorization = `Bearer ${currentUser.Token}`;
        }
    } catch {
        return headers;
    }

    return headers;
};

/** Normalizes CMS search payloads into a plain array of news items. */
const normalizeNewsSearchResults = (response) => {
    const payload = normalizeApiResponse(response);

    if (Array.isArray(payload)) {
        return payload;
    }

    if (!payload || typeof payload !== 'object') {
        return [];
    }

    if (Array.isArray(payload.Items)) return payload.Items;
    if (Array.isArray(payload.Data)) return payload.Data;
    if (Array.isArray(payload.data)) return payload.data;
    if (Array.isArray(payload.Results)) return payload.Results;

    if (payload.ID !== undefined && payload.ID !== null) {
        return [payload];
    }

    return [];
};

const normalizeApiDate = (value) => {
    if (!value || String(value).startsWith('0001-01-01')) return undefined;
    return value;
};

const isApiNewsId = (id) => {
    const numericId = Number(id);
    return Number.isInteger(numericId) && numericId > 0;
};

/** API field names duplicated on merged save payloads; GUI fields must win. */
const NEWS_STALE_API_FIELDS = [
    'CoverImageID',
    'FormatID',
    'LinkTargetID',
    'LinkTargetId',
    'LinkTargetType',
    'LinkTargetUrl',
    'ModelIDs',
    'SectionID',
    'TagIDs',
];

const stripStaleNewsApiFields = (news = {}) => {
    NEWS_STALE_API_FIELDS.forEach((fieldName) => {
        delete news[fieldName];
    });
    return news;
};

const hasGuiLinkTarget = (linkTarget) => {
    return Boolean(linkTarget && linkTarget.type !== undefined && linkTarget.type !== null);
};

const getGuiLinkTarget = (news = {}) => {
    return hasGuiLinkTarget(news.LinkTarget) ? news.LinkTarget : buildNewsLinkTargetFromApi(news);
};

const getApiNewsModelIds = (news = {}) => {
    if (Array.isArray(news.ModelIds)) return news.ModelIds;
    if (Array.isArray(news.ModelIDs)) return news.ModelIDs;
    if (Array.isArray(news.Models)) {
        return news.Models.map((model) => model.ID).filter(Boolean);
    }
    return [];
};

const getApiNewsTagIds = (news = {}) => {
    // Prefer GUI TagIds: save merges can leave stale TagIDs from the GET payload.
    if (Array.isArray(news.TagIds)) return news.TagIds;
    if (Array.isArray(news.TagIDs)) return news.TagIDs;
    return [];
};

const getApiNewsSectionId = (news = {}) => {
    if (news.SectionId !== undefined && news.SectionId !== null) return news.SectionId;
    if (news.SectionID !== undefined && news.SectionID !== null) return news.SectionID;
    return undefined;
};

const getApiNewsCoverImageId = (news = {}) => {
    const coverDocumentId = getArtMediaDocumentId(news.CoverImage || {});
    if (coverDocumentId) return coverDocumentId;
    if (news.CoverImage?.ID !== undefined && news.CoverImage?.ID !== null) return news.CoverImage.ID;
    if (news.CoverImageID !== undefined && news.CoverImageID !== null) return news.CoverImageID;
    return undefined;
};

const normalizeNewsCoverImageFromApi = (news = {}) => {
    const documentId = getApiNewsCoverImageId(news);
    if (!documentId) return news.CoverImage || {};

    return {
        ...(news.CoverImage || {}),
        documentId,
    };
};

/** Normalizes a CMS news payload into the editor/list shape used in the GUI. */
const normalizeNewsFromApi = (news = {}) => {
    const sectionId = getApiNewsSectionId(news);
    const sectionOrder = getSafeNewsSectionOrder(news.SectionOrder);

    return {
        ...news,
        Abstract: news.Abstract || '',
        CoverImage: normalizeNewsCoverImageFromApi(news),
        CreatedAt: normalizeApiDate(news.CreatedAt),
        // Il FormatId della GUI vince sul campo API; null, anche quello letto dall'API, resta null.
        FormatId:
            news.FormatId === null || (news.FormatId === undefined && news.FormatID === null)
                ? null
                : (news.FormatId ?? news.FormatID ?? ''),
        LinkTarget: getGuiLinkTarget(news),
        ModelIds: getApiNewsModelIds(news),
        SectionFocus: sectionId ? Boolean(news.SectionFocus) : false,
        SectionId: sectionId,
        SectionName: news.SectionName,
        SectionOrder: sectionId ? sectionOrder : undefined,
        Status: normalizeNewsStatus(news.Status),
        TagIds: getApiNewsTagIds(news),
        UpdatedAt: normalizeApiDate(news.UpdatedAt),
    };
};

const buildNewsSearchPayload = (filters = {}) => {
    const text = filters.text ?? filters.Text;
    const userCreated = filters.userCreated ?? filters.UserCreated;
    const sectionId = filters.sectionId ?? filters.SectionID;
    const modelId = filters.modelId ?? filters.ModelID;
    const status = toApiNewsStatusFilter(filters.status ?? filters.Status);

    return {
        ModelID: modelId ?? null,
        SectionID: sectionId ?? null,
        Status: status,
        Text: text ? String(text).trim() : null,
        UserCreated: userCreated ?? null,
    };
};

const getApiLinkTargetFields = (news = {}) => {
    const linkTarget = getGuiLinkTarget(news);
    const linkType = normalizeNewsLinkTarget(linkTarget.type);
    const linkTargetType = toApiNewsLinkTargetType(linkType);

    if (!linkTargetType) {
        return {
            LinkTargetID: null,
            LinkTargetType: null,
            LinkTargetUrl: null,
        };
    }

    if (linkType === NEWS_LINK_TARGET.EXTERNAL) {
        return {
            LinkTargetID: null,
            LinkTargetType: linkTargetType,
            LinkTargetUrl: linkTarget.externalUrl || null,
        };
    }

    const linkTargetId = linkTarget.articleId ?? linkTarget.galleryId ?? linkTarget.modelId ?? null;

    return {
        LinkTargetID: linkTargetId !== undefined && linkTargetId !== null ? String(linkTargetId) : null,
        LinkTargetType: linkTargetType,
        LinkTargetUrl: null,
    };
};

const buildNewsSavePayload = (news = {}) => {
    const normalizedNews = normalizeNewsFromApi(news);
    const sectionId = getApiNewsSectionId(normalizedNews);
    const coverImageId = getApiNewsCoverImageId(normalizedNews);
    const linkTargetFields = getApiLinkTargetFields(normalizedNews);
    const now = new Date().toISOString();

    return {
        Abstract: normalizedNews.Abstract || '',
        CoverImageID: coverImageId ? Number(coverImageId) : null,
        CreatedAt: normalizedNews.CreatedAt || now,
        FormatID: normalizedNews.FormatId || null,
        LinkTargetID: linkTargetFields.LinkTargetID,
        LinkTargetType: linkTargetFields.LinkTargetType,
        LinkTargetUrl: linkTargetFields.LinkTargetUrl,
        ModelIDs: getApiNewsModelIds(normalizedNews).map((modelId) => Number(modelId)).filter(Number.isFinite),
        SectionFocus: sectionId ? Boolean(normalizedNews.SectionFocus) : false,
        SectionID: sectionId ? Number(sectionId) : 0,
        SectionOrder: sectionId ? Number(normalizedNews.SectionOrder) || 0 : 0,
        Status: toApiNewsStatus(normalizedNews.Status),
        TagIDs: getApiNewsTagIds(normalizedNews).map((tagId) => Number(tagId)).filter(Number.isFinite),
        Title: normalizedNews.Title || '',
        UpdatedAt: now,
    };
};

const sortNewsBySectionOrder = (news = []) => {
    return [...news].sort((firstNews, secondNews) => {
        const firstPosition = getSafeNewsSectionOrder(firstNews.SectionOrder) ?? Number.MAX_SAFE_INTEGER;
        const secondPosition = getSafeNewsSectionOrder(secondNews.SectionOrder) ?? Number.MAX_SAFE_INTEGER;
        if (firstPosition === secondPosition) {
            return String(firstNews.UpdatedAt || '').localeCompare(String(secondNews.UpdatedAt || ''));
        }
        return firstPosition - secondPosition;
    });
};

export const searchNewsApi = async (filters = {}) => {
    const response = await Axios.post(NEWS_SEARCH_API, buildNewsSearchPayload(filters));
    const newsItems = normalizeNewsSearchResults(response);
    const normalizedNews = newsItems.map((item) => normalizeNewsFromApi(item));
    if (filters.sectionId ?? filters.SectionID) {
        return sortNewsBySectionOrder(normalizedNews);
    }

    return normalizedNews;
};

export const loadNewsById = async (newsId) => {
    const response = await Axios.get(`${NEWS_API_BASE}/${newsId}`);
    return normalizeNewsFromApi(normalizeApiResponse(response));
};

export const loadNewsApi = async (filters) => {
    return searchNewsApi(filters);
};

export const saveNewsApi = async (news) => {
    let newsToSave = { ...news };

    if (isApiNewsId(news.ID)) {
        const currentNews = await loadNewsById(news.ID);
        newsToSave = {
            ...currentNews,
            ...news,
            CreatedAt: currentNews.CreatedAt,
        };
    }

    stripStaleNewsApiFields(newsToSave);

    const payload = buildNewsSavePayload(newsToSave);
    let savedId = isApiNewsId(newsToSave.ID) ? Number(newsToSave.ID) : null;

    if (savedId) {
        await Axios.put(`${NEWS_API_BASE}/${savedId}`, payload);
    } else {
        const response = await Axios.post(NEWS_API_BASE, payload);
        const saved = normalizeApiResponse(response);
        savedId = saved?.ID ?? saved?.id;
    }

    if (!savedId) {
        throw new Error('Invalid news save response');
    }

    return loadNewsById(savedId);
};

export const removeNewsApi = async (newsID) => {
    await Axios.delete(`${NEWS_API_BASE}/${newsID}`);
};

export const sortNewsApi = async (newsIds = []) => {
    const response = await fetch(`${getBaseUrl()}${NEWS_SORT_API}`, {
        method: 'PUT',
        headers: getJsonRequestHeaders(),
        body: JSON.stringify({
            NewsItemIDs: newsIds,
        }),
    });

    if (!response.ok) {
        const error = new Error('Unable to sort news');
        error.status = response.status;
        throw error;
    }

    return response;
};

const parseSectionFormatsFromApi = (formatsValue) => {
    if (Array.isArray(formatsValue)) {
        return formatsValue;
    }

    // null distingue un valore assente da un array vuoto, che significa nessuna cover.
    if (typeof formatsValue !== 'string' || !formatsValue.trim()) {
        return null;
    }

    try {
        const parsed = JSON.parse(formatsValue);
        return Array.isArray(parsed) ? parsed : null;
    } catch (error) {
        return null;
    }
};

const stringifySectionFormatsForApi = (formats) => {
    return JSON.stringify(normalizeNewsFormats(formats));
};

const normalizeNewsSectionsListResponse = (response) => {
    const payload = normalizeApiResponse(response);

    if (Array.isArray(payload)) {
        return payload;
    }

    if (!payload || typeof payload !== 'object') {
        return [];
    }

    if (Array.isArray(payload.Items)) return payload.Items;
    if (Array.isArray(payload.Data)) return payload.Data;
    if (payload.ID !== undefined && payload.ID !== null) return [payload];

    return [];
};

/** Normalizes a CMS news section payload into the editor/list shape used in the GUI. */
const normalizeNewsSectionFromApi = (section = {}) => {
    const parsedFormats = parseSectionFormatsFromApi(section.Formats ?? section.NewsFormats);

    return {
        ...section,
        CreatedAt: normalizeApiDate(section.CreatedAt),
        ID: section.ID,
        Name: normalizeName(section.Name),
        NewsFormats: normalizeNewsFormats(parsedFormats),
        NewsItemsCount: Number(section.NewsItemsCount) || 0,
        UpdatedAt: normalizeApiDate(section.UpdatedAt),
    };
};

const buildNewsSectionSavePayload = (section = {}) => {
    const formatsSource = section.NewsFormats ?? parseSectionFormatsFromApi(section.Formats);

    return {
        Formats: stringifySectionFormatsForApi(formatsSource),
        ID: isApiNewsId(section.ID) ? Number(section.ID) : 0,
        Name: normalizeName(section.Name),
    };
};

export const loadNewsSectionsApi = async () => {
    const response = await Axios.get(NEWS_SECTIONS_API_BASE);
    const sections = normalizeNewsSectionsListResponse(response);
    return sections.map((section) => normalizeNewsSectionFromApi(section));
};

export const saveNewsSectionApi = async (section) => {
    const payload = buildNewsSectionSavePayload(section);

    if (isApiNewsId(section.ID)) {
        await Axios.put(`${NEWS_SECTIONS_API_BASE}/${section.ID}`, payload);
        return normalizeNewsSectionFromApi({
            ...section,
            Formats: payload.Formats,
            Name: payload.Name,
        });
    }

    const response = await Axios.post(NEWS_SECTIONS_API_BASE, {
        Formats: payload.Formats,
        Name: payload.Name,
    });
    const saved = normalizeApiResponse(response);
    const savedId = saved?.ID ?? saved?.id;

    if (savedId) {
        return normalizeNewsSectionFromApi({
            ...section,
            Formats: payload.Formats,
            ID: savedId,
            Name: payload.Name,
        });
    }

    const sections = await loadNewsSectionsApi();
    const savedSection = sections.find((item) => item.Name === payload.Name);
    if (!savedSection) {
        throw new Error('Invalid news section save response');
    }

    return savedSection;
};

export const saveNewsSectionsApi = async (sections) => {
    const savedSections = [];
    for (let index = 0; index < sections.length; index += 1) {
        savedSections.push(await saveNewsSectionApi(sections[index]));
    }
    return savedSections;
};

export const removeNewsSectionApi = async (sectionID) => {
    const sections = await loadNewsSectionsApi();
    const section = sections.find((item) => String(item.ID) === String(sectionID));

    if (section && Number(section.NewsItemsCount) > 0) {
        throw Object.assign(new Error('Cannot delete a section that still contains news items'), {
            code: NEWS_SECTION_NOT_EMPTY,
        });
    }

    await Axios.delete(`${NEWS_SECTIONS_API_BASE}/${sectionID}`);
};

export const loadNewsTagsApi = async () => {
    return Axios.get(NEWS_TAGS_API_URL);
};

export const saveNewsTagApi = async (tag) => {
    if (tag.ID) {
        return Axios.put(`${NEWS_TAGS_API_URL}/${tag.ID}`, tag);
    }
    return Axios.post(NEWS_TAGS_API_URL, tag);
};

export const removeNewsTagApi = async (tagID) => {
    return Axios.delete(`${NEWS_TAGS_API_URL}/${tagID}`);
};

export const ensureNewsTagsApi = async (tagValues = []) => {
    return Axios.post(`${NEWS_TAGS_API_URL}/ensure`, { tags: tagValues });
};

export const loadNews = async (_mode, filters) => loadNewsApi(filters);

export const saveNews = async (news) => saveNewsApi(news);

export const sortNews = async (newsIds) => sortNewsApi(newsIds);

export const removeNews = async (newsID) => removeNewsApi(newsID);

export const loadNewsSections = async () => loadNewsSectionsApi();

export const saveNewsSection = async (section) => saveNewsSectionApi(section);

export const saveNewsSections = async (sections) => saveNewsSectionsApi(sections);

export const removeNewsSection = async (sectionID) => removeNewsSectionApi(sectionID);

export const loadNewsTags = async () => loadNewsTagsApi();

export const saveNewsTag = async (tag) => saveNewsTagApi(tag);

export const removeNewsTag = async (tagID) => removeNewsTagApi(tagID);

export const ensureNewsTags = async (tagValues) => ensureNewsTagsApi(tagValues);
